import React, { useRef, useEffect } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

function SortableMenuItem({
  item,
  level,
  index,
  onEdit,
  onDelete,
  onAddChild,
  onPromoteToRoot,
  expanded,
  onToggleExpanded,
  hasChildren,
  children,
  isOver,
  isDragging: isDraggingProp,
  disabled
}) {
  const itemRef = useRef(null)
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    data,
    setData
  } = useSortable({
    id: item.id,
    disabled: disabled,
    data: {
      type: 'item',
      parentId: item.parent_id,
      index: index,
      element: null
    }
  })

  // Update data with element reference
  useEffect(() => {
    if (itemRef.current && setData) {
      setData({
        type: 'item',
        parentId: item.parent_id,
        index: index,
        element: itemRef.current
      })
    }
  }, [item.parent_id, index, setData])

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-2 ${isDragging ? 'z-50' : ''}`}
      data-sortable-id={item.id}
    >
      <div 
        ref={itemRef} 
        className={`group bg-white dark:bg-gray-700 border rounded-lg shadow-sm hover:shadow-md transition-all ${
          isOver 
            ? 'border-blue-500 dark:border-blue-400 border-2 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-gray-200 dark:border-gray-600'
        }`}
      >
        <div className="flex items-center p-3">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className={`mr-3 ${
              disabled 
                ? 'cursor-not-allowed text-gray-300 dark:text-gray-600' 
                : 'cursor-move text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </div>

          {/* Expand/Collapse Button */}
          {hasChildren && (
            <button
              onClick={() => onToggleExpanded(item.id)}
              className="mr-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <svg className={`w-4 h-4 transform transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Menu Item Info */}
          <div className="flex-1">
            <div className="flex items-center">
              <span className="font-medium text-gray-900 dark:text-white">
                {item.name}
              </span>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {item.path ? (
                <span>
                  <span className="font-medium">Path:</span> {item.path}
                </span>
              ) : (
                <span className="italic">Header only</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className={`flex items-center space-x-2 transition-opacity ${
            disabled ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'
          }`}>
            {/* Promote to Root - only show if item has a parent */}
            {item.parent_id && (
              <button
                onClick={() => onPromoteToRoot(item.id)}
                className="p-1 text-purple-400 hover:text-purple-600 dark:text-purple-500 dark:hover:text-purple-300 disabled:cursor-not-allowed disabled:hover:text-purple-400"
                title="Promote to root level"
                disabled={disabled}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
              </button>
            )}
            <button
              onClick={() => onAddChild(item.id)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 disabled:cursor-not-allowed disabled:hover:text-gray-400"
              title="Add child item"
              disabled={disabled}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              onClick={() => onEdit(item)}
              className="p-1 text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300 disabled:cursor-not-allowed disabled:hover:text-blue-400"
              title="Edit item"
              disabled={disabled}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => onDelete(item.id)}
              className="p-1 text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300 disabled:cursor-not-allowed disabled:hover:text-red-400"
              title="Delete item"
              disabled={disabled}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Children */}
      {expanded && children && (
        <div className="mt-2">
          {children}
        </div>
      )}
    </div>
  )
}

export default SortableMenuItem